import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  createAmericanoMatches,
  createGroupMatches,
  createKnockoutMatches,
  createLeagueMatches,
  getGroupMatches,
  getGroupStandings,
  getKnockoutMatches,
  getLeagueMatches,
  getLeagueStandings,
  getStageDetails,
  previewAmericanoMatches,
  previewGroupMatches,
  previewKnockoutMatches,
  previewLeagueMatches,
} from "../../api/tournaments";
import { IPrecedingStage, StageDetails } from "../../types/tournament";
import { IMatchDetailDTO } from "../../models/IMatch";
import { getLetterByIndex, isAmericanoFormat, americanoFullCycleRounds } from "../../utils/methods";
import { ILeagueStanding } from "../../models/ILeague";
import { IGroupStandingList } from "../../models/IGroup";
import { formatDateLocal } from "../../utils/dateFormat";
import { rankGroupTeams } from "../../utils/seeding";
import {
  StepType,
  MatchConfig,
  GroupDateAssignment,
  VenueAssignment,
  RankedGroupTeam,
  GroupDefinition,
  VenueRange,
} from "./types";
import {
  getCourtNumber,
  validateMatchConfigurations,
  scheduleGroupMatchesWithSpecificDates,
  scheduleGroupMatchesWithVenues,
  scheduleKnockoutMatchesWithVenues,
  scheduleLeagueMatchesWithVenues,
  scheduleGroupMatchesStandard,
  scheduleAmericanoRounds,
  scheduleMatchesStandard,
} from "./scheduling";
import { getDependentMatchLabel } from "./bracketPreview";
import { useDialogs } from "../../ui";

// Auto-assign courts within each round: the matches of a round land on Court 1, Court 2, …
// in generation order (still editable per match in the scheduling grid).
const withAutoCourts = (data: IMatchDetailDTO[]): IMatchDetailDTO[] => {
  const seen: { [round: string]: number } = {};
  return data.map((m) => {
    const round = m.round || "";
    const idx = seen[round] ?? 0;
    seen[round] = idx + 1;
    return { ...m, venue: `Court ${idx + 1}` };
  });
};

export const useCreateSchedule = () => {
  const navigate = useNavigate();
  // The brand alert in place of window.alert (same messages)
  const { alert } = useDialogs();
  const [searchParams] = useSearchParams();
  const { id, stageId, tournamentCategoryId } = useParams();

  const [createLoading, setCreateLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [stage, setStage] = useState<StageDetails>();
  const [currentStep, setCurrentStep] = useState<StepType>("dependent-stages");

  const [activeRound, setActiveRound] = useState<{ name: string; order: number }>();
  const [activeSchedulingRound, setActiveSchedulingRound] = useState<{ name: string; order: number }>();

  const [stageDependent, setStageDependent] = useState(true);
  const [activeStageTab, setActiveStageTab] = useState<IPrecedingStage>();
  const [selectedStages, setSelectedStages] = useState<IPrecedingStage[]>([]);
  const [selectedTeams, setSelectedTeams] = useState<string[]>([]);
  const [numOfGroups, setNumOfGroups] = useState<number>(1);
  const [createWithEmptyTeams, setCreateWithEmptyTeams] = useState(false);

  const [rankedGroupTeams, setRankedGroupTeams] = useState<RankedGroupTeam[]>([]);
  const [selectedType, setSelectedType] = useState<"manual" | "random" | "seeded">("random");
  const [groups, setGroups] = useState<GroupDefinition[]>([]);

  const [previewLoading, setPreviewLoading] = useState(false);
  const [rankedTeamsLoading, setRankedTeamsLoading] = useState(false);
  const [bracketMatches, setBracketMatches] = useState<IMatchDetailDTO[]>([]);
  const [isRandomMatches, setIsRandomMatches] = useState(false);

  const [matches, setMatches] = useState<IMatchDetailDTO[]>([]);

  // Americano/Mexicano schedule config (known only after the first preview reveals the
  // player count). courts = max matches per round; rounds default to a full cycle (U - 1).
  const [americanoCourts, setAmericanoCourts] = useState<number | undefined>(undefined);
  const [americanoRounds, setAmericanoRounds] = useState<number | undefined>(undefined);
  const [americanoUnitCount, setAmericanoUnitCount] = useState<number>(0);
  // Once the admin edits Rounds manually, stop auto-defaulting it from players/courts.
  const [americanoRoundsManual, setAmericanoRoundsManual] = useState(false);

  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [matchConfigs, setMatchConfigs] = useState<{ [key: string]: MatchConfig }>({});

  const [groupDateAssignments, setGroupDateAssignments] = useState<{ [groupId: string]: GroupDateAssignment }>({});

  const [groupVenueAssignments, setGroupVenueAssignments] = useState<{ [groupId: string]: VenueAssignment }>({});
  const [knockoutVenueAssignments, setKnockoutVenueAssignments] = useState<{ [roundName: string]: VenueAssignment }>(
    {},
  );
  const [leagueVenueAssignments, setLeagueVenueAssignments] = useState<VenueAssignment>({
    venueIds: [],
    venueNames: [],
  });

  const [venueRange, setVenueRange] = useState<VenueRange>({ start: 1, end: 8 });
  const [frequencyDirty, setFrequencyDirty] = useState(true);
  const [dependentStageLastMatchTime, setDependentStageLastMatchTime] = useState<Date | null>(null);

  // --- Venue range calculations ---

  const calculateTotalVenuesNeeded = () => {
    let totalVenues = 0;
    Object.values(matchConfigs).forEach((config) => {
      if (config.venueCount) totalVenues = Math.max(totalVenues, config.venueCount);
    });
    Object.values(groupDateAssignments).forEach((groupAssignment) => {
      Object.values(groupAssignment.configs).forEach((config) => {
        if (config.venueCount) totalVenues = Math.max(totalVenues, config.venueCount);
      });
    });
    return totalVenues || 1;
  };

  useEffect(() => {
    const totalVenues = calculateTotalVenuesNeeded();
    setVenueRange({ start: 1, end: totalVenues });
  }, [matchConfigs, groupDateAssignments]);

  // --- Venue range filtering ---

  useEffect(() => {
    setLeagueVenueAssignments((prev) => {
      if (prev.venueIds.length === 0) return prev;
      const filteredVenueIds: string[] = [];
      const filteredVenueNames: string[] = [];
      prev.venueIds.forEach((venueId, index) => {
        const venueName = prev.venueNames[index];
        const courtNumber = getCourtNumber(venueName);
        if (courtNumber !== null && courtNumber >= venueRange.start && courtNumber <= venueRange.end) {
          filteredVenueIds.push(venueId);
          filteredVenueNames.push(venueName);
        }
      });
      if (filteredVenueIds.length !== prev.venueIds.length) {
        return { venueIds: filteredVenueIds, venueNames: filteredVenueNames };
      }
      return prev;
    });

    setGroupVenueAssignments((prev) => {
      let hasChanges = false;
      const updated: typeof prev = {};
      Object.keys(prev).forEach((groupId) => {
        const assignment = prev[groupId];
        if (assignment.venueIds.length > 0) {
          const filteredVenueIds: string[] = [];
          const filteredVenueNames: string[] = [];
          assignment.venueIds.forEach((venueId, index) => {
            const venueName = assignment.venueNames[index];
            const courtNumber = getCourtNumber(venueName);
            if (courtNumber !== null && courtNumber >= venueRange.start && courtNumber <= venueRange.end) {
              filteredVenueIds.push(venueId);
              filteredVenueNames.push(venueName);
            }
          });
          if (filteredVenueIds.length !== assignment.venueIds.length) {
            hasChanges = true;
            updated[groupId] = { venueIds: filteredVenueIds, venueNames: filteredVenueNames };
          } else {
            updated[groupId] = assignment;
          }
        } else {
          updated[groupId] = assignment;
        }
      });
      return hasChanges ? updated : prev;
    });

    setKnockoutVenueAssignments((prev) => {
      let hasChanges = false;
      const updated: typeof prev = {};
      Object.keys(prev).forEach((roundName) => {
        const assignment = prev[roundName];
        if (assignment.venueIds.length > 0) {
          const filteredVenueIds: string[] = [];
          const filteredVenueNames: string[] = [];
          assignment.venueIds.forEach((venueId, index) => {
            const venueName = assignment.venueNames[index];
            const courtNumber = getCourtNumber(venueName);
            if (courtNumber !== null && courtNumber >= venueRange.start && courtNumber <= venueRange.end) {
              filteredVenueIds.push(venueId);
              filteredVenueNames.push(venueName);
            }
          });
          if (filteredVenueIds.length !== assignment.venueIds.length) {
            hasChanges = true;
            updated[roundName] = { venueIds: filteredVenueIds, venueNames: filteredVenueNames };
          } else {
            updated[roundName] = assignment;
          }
        } else {
          updated[roundName] = assignment;
        }
      });
      return hasChanges ? updated : prev;
    });
  }, [venueRange.start, venueRange.end]);

  // --- Date management ---

  const addDate = (date: Date) => {
    const formatted = formatDateLocal(date);
    if (!selectedDates.find((d) => formatDateLocal(d) === formatted)) {
      const startTime = new Date(date);
      if (stageDependent && dependentStageLastMatchTime) {
        startTime.setHours(dependentStageLastMatchTime.getHours(), dependentStageLastMatchTime.getMinutes(), 0, 0);
        startTime.setMinutes(startTime.getMinutes() + 30);
      } else {
        startTime.setHours(19, 0, 0, 0);
      }
      const totalMatches = matches.length;
      const newDayCount = selectedDates.length + 1;

      setSelectedDates([...selectedDates, date]);
      setMatchConfigs((prev) => {
        const updated = { ...prev };
        if (totalMatches > 0) {
          // Distribute matches evenly across all days (including the new one)
          const basePerDay = Math.floor(totalMatches / newDayCount);
          let remainder = totalMatches % newDayCount;

          // Recalculate existing dates
          const existingDateStrs = selectedDates.map((d) => formatDateLocal(d));
          for (const dateStr of existingDateStrs) {
            if (updated[dateStr]) {
              updated[dateStr] = { ...updated[dateStr], matchesPerDay: basePerDay + (remainder > 0 ? 1 : 0) };
              if (remainder > 0) remainder--;
            }
          }

          // Set for the new date
          updated[formatted] = { matchesPerDay: basePerDay + (remainder > 0 ? 1 : 0), interval: 30, startTime, venueCount: 1, bestOf: 1 };
        } else {
          updated[formatted] = { matchesPerDay: 3, interval: 30, startTime, venueCount: 1, bestOf: 1 };
        }
        return updated;
      });
      setFrequencyDirty(true);
    }
  };

  const addGroupDate = (groupId: string, date: Date) => {
    const formatted = formatDateLocal(date);
    const startTime = new Date(date);
    if (stageDependent && dependentStageLastMatchTime) {
      startTime.setHours(dependentStageLastMatchTime.getHours(), dependentStageLastMatchTime.getMinutes(), 0, 0);
      startTime.setMinutes(startTime.getMinutes() + 30);
    } else {
      startTime.setHours(19, 0, 0, 0);
    }

    setGroupDateAssignments((prev) => {
      const currentGroup = prev[groupId] || { dates: [], configs: {} };
      if (!currentGroup.dates.find((d) => formatDateLocal(d) === formatted)) {
        // Inherit config from the general date config if available
        const generalConfig = matchConfigs[formatted];
        const config: MatchConfig = generalConfig
          ? { ...generalConfig }
          : { matchesPerDay: matches.filter((m) => m.group === groupId).length || 3, interval: 30, startTime, venueCount: 1, bestOf: 1 };
        return {
          ...prev,
          [groupId]: {
            dates: [...currentGroup.dates, date],
            configs: {
              ...currentGroup.configs,
              [formatted]: config,
            },
          },
        };
      }
      return prev;
    });
    setFrequencyDirty(true);
  };

  const removeGroupDate = (groupId: string, dateStr: string) => {
    setGroupDateAssignments((prev) => {
      const currentGroup = prev[groupId];
      if (!currentGroup) return prev;
      const updatedDates = currentGroup.dates.filter((d) => formatDateLocal(d) !== dateStr);
      const updatedConfigs = { ...currentGroup.configs };
      delete updatedConfigs[dateStr];
      return { ...prev, [groupId]: { dates: updatedDates, configs: updatedConfigs } };
    });
    setFrequencyDirty(true);
  };

  const updateConfig = (
    dateStr: string,
    field: "matchesPerDay" | "interval" | "startTime" | "venueCount" | "bestOf",
    value: number | Date,
  ) => {
    setMatchConfigs((prev) => ({
      ...prev,
      [dateStr]: { ...prev[dateStr], [field]: value },
    }));
    setGroupDateAssignments((prev) => {
      const updated = { ...prev };
      Object.keys(updated).forEach((groupId) => {
        const groupAssignment = updated[groupId];
        if (groupAssignment.configs[dateStr]) {
          updated[groupId] = {
            ...groupAssignment,
            configs: {
              ...groupAssignment.configs,
              [dateStr]: { ...groupAssignment.configs[dateStr], [field]: value },
            },
          };
        }
      });
      return updated;
    });
    setFrequencyDirty(true);
  };

  const removeDate = (dateStr: string) => {
    setSelectedDates((prev) => prev.filter((d) => formatDateLocal(d) !== dateStr));
    setMatchConfigs((prev) => {
      const newConfigs = { ...prev };
      delete newConfigs[dateStr];

      // Redistribute matches evenly across remaining days
      const totalMatches = matches.length;
      const remainingDateStrs = Object.keys(newConfigs);
      if (totalMatches > 0 && remainingDateStrs.length > 0) {
        const basePerDay = Math.floor(totalMatches / remainingDateStrs.length);
        let remainder = totalMatches % remainingDateStrs.length;
        for (const ds of remainingDateStrs) {
          newConfigs[ds] = { ...newConfigs[ds], matchesPerDay: basePerDay + (remainder > 0 ? 1 : 0) };
          if (remainder > 0) remainder--;
        }
      }

      return newConfigs;
    });
    setFrequencyDirty(true);
  };

  // --- Venue assignment functions ---

  const updateGroupVenueAssignment = (
    groupId: string,
    venueId: string,
    venueName: string,
    isAdding: boolean = true,
  ) => {
    setGroupVenueAssignments((prev) => {
      const current = prev[groupId] || { venueIds: [], venueNames: [] };
      if (isAdding) {
        if (!current.venueIds.includes(venueId)) {
          return {
            ...prev,
            [groupId]: { venueIds: [...current.venueIds, venueId], venueNames: [...current.venueNames, venueName] },
          };
        }
      } else {
        const venueIndex = current.venueIds.indexOf(venueId);
        if (venueIndex > -1) {
          const newVenueIds = [...current.venueIds];
          const newVenueNames = [...current.venueNames];
          newVenueIds.splice(venueIndex, 1);
          newVenueNames.splice(venueIndex, 1);
          return { ...prev, [groupId]: { venueIds: newVenueIds, venueNames: newVenueNames } };
        }
      }
      return prev;
    });
    setFrequencyDirty(true);
  };

  const autoAssignVenuesToGroups = () => {
    if (groups.length === 0) return;
    const totalVenues = venueRange.end - venueRange.start + 1;
    const newAssignments: { [groupId: string]: VenueAssignment } = {};
    groups.forEach((group, index) => {
      const venuesNeeded = Math.max(1, Math.ceil(group.teamIds.length / 4));
      const venueIds: string[] = [];
      const venueNames: string[] = [];
      for (let i = 0; i < Math.min(venuesNeeded, totalVenues); i++) {
        const venueNumber = venueRange.start + ((index + i) % totalVenues);
        venueIds.push(`venue-${venueNumber}`);
        venueNames.push(`Court ${venueNumber}`);
      }
      newAssignments[group.groupName] = { venueIds, venueNames };
    });
    setGroupVenueAssignments(newAssignments);
    setFrequencyDirty(true);
  };

  const updateKnockoutVenueAssignment = (
    roundName: string,
    venueId: string,
    venueName: string,
    isAdding: boolean = true,
  ) => {
    setKnockoutVenueAssignments((prev) => {
      const current = prev[roundName] || { venueIds: [], venueNames: [] };
      if (isAdding) {
        if (!current.venueIds.includes(venueId)) {
          return {
            ...prev,
            [roundName]: { venueIds: [...current.venueIds, venueId], venueNames: [...current.venueNames, venueName] },
          };
        }
      } else {
        const venueIndex = current.venueIds.indexOf(venueId);
        if (venueIndex > -1) {
          const newVenueIds = [...current.venueIds];
          const newVenueNames = [...current.venueNames];
          newVenueIds.splice(venueIndex, 1);
          newVenueNames.splice(venueIndex, 1);
          return { ...prev, [roundName]: { venueIds: newVenueIds, venueNames: newVenueNames } };
        }
      }
      return prev;
    });
    setFrequencyDirty(true);
  };

  const autoAssignVenuesToKnockoutRounds = () => {
    if (bracketMatches.length === 0) return;
    const uniqueRounds = Array.from(new Set<string>(bracketMatches.map((x) => x.round)));
    const totalVenues = venueRange.end - venueRange.start + 1;
    const newAssignments: { [roundName: string]: VenueAssignment } = {};
    uniqueRounds.forEach((roundName) => {
      const roundMatches = bracketMatches.filter((x) => x.round === roundName);
      const venuesNeeded = Math.max(1, Math.min(roundMatches.length, totalVenues));
      const venueIds: string[] = [];
      const venueNames: string[] = [];
      for (let i = 0; i < venuesNeeded; i++) {
        const venueNumber = venueRange.start + (i % totalVenues);
        venueIds.push(`venue-${venueNumber}`);
        venueNames.push(`Court ${venueNumber}`);
      }
      newAssignments[roundName] = { venueIds, venueNames };
    });
    setKnockoutVenueAssignments(newAssignments);
    setFrequencyDirty(true);
  };

  const updateLeagueVenueAssignment = (venueId: string, venueName: string, isAdding: boolean = true) => {
    setLeagueVenueAssignments((prev) => {
      const current = prev || { venueIds: [], venueNames: [] };
      if (isAdding) {
        if (!current.venueIds.includes(venueId)) {
          return { venueIds: [...current.venueIds, venueId], venueNames: [...current.venueNames, venueName] };
        }
      } else {
        const venueIndex = current.venueIds.indexOf(venueId);
        if (venueIndex > -1) {
          const newVenueIds = [...current.venueIds];
          const newVenueNames = [...current.venueNames];
          newVenueIds.splice(venueIndex, 1);
          newVenueNames.splice(venueIndex, 1);
          return { venueIds: newVenueIds, venueNames: newVenueNames };
        }
      }
      return prev;
    });
    setFrequencyDirty(true);
  };

  const autoAssignVenuesToLeague = () => {
    if (matches.length === 0) return;
    const totalVenues = venueRange.end - venueRange.start + 1;
    const venuesNeeded = Math.max(1, Math.min(Math.ceil(matches.length / 10), totalVenues));
    const venueIds: string[] = [];
    const venueNames: string[] = [];
    for (let i = 0; i < venuesNeeded; i++) {
      const venueNumber = venueRange.start + (i % totalVenues);
      venueIds.push(`venue-${venueNumber}`);
      venueNames.push(`Court ${venueNumber}`);
    }
    setLeagueVenueAssignments({ venueIds, venueNames });
    setFrequencyDirty(true);
  };

  // --- Data fetching ---

  const getStageOverview = async () => {
    try {
      const res = await getStageDetails(id!, stageId!);
      setStage(res.data);
      if (res.data.precedingStages.length === 0) setStageDependent(false);
    } catch (error: any) {
      console.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const getStandings = async (format: string, stageIdParam: string) => {
    try {
      if (format === "Group") {
        const res = await getGroupStandings(stageIdParam);
        return res.data;
      } else if (format === "League") {
        const res = await getLeagueStandings(stageIdParam);
        return res.data;
      }
    } catch (error) {
      console.error("error retrieving standings table");
    }
  };

  const fetchAndRankGroupTeams = async (stageIdParam: string) => {
    setRankedTeamsLoading(true);
    try {
      const res = await getGroupStandings(stageIdParam);
      const rankedTeams = rankGroupTeams(res.data as IGroupStandingList[]);

      setRankedGroupTeams(rankedTeams);
      return rankedTeams;
    } catch (error) {
      console.error("Error fetching and ranking group teams:", error);
      return [];
    } finally {
      setRankedTeamsLoading(false);
    }
  };

  const fetchDependentStageLastMatchTime = async () => {
    try {
      let latestTime: Date | null = null;

      for (const stg of selectedStages) {
        let stageMatches: IMatchDetailDTO[] = [];

        if (stg.stageName === "League") {
          const res = await getLeagueMatches(stg.id);
          stageMatches = res.data.matches || [];
        } else if (stg.stageName === "Group") {
          const res = await getGroupMatches(stg.id);
          stageMatches = res.data.matches || [];
        } else if (stg.stageName === "Knockout") {
          const res = await getKnockoutMatches(stg.id);
          stageMatches = res.data.matches || [];
        }

        for (const match of stageMatches) {
          if (match.startTime) {
            const matchTime = new Date(match.startTime);
            if (!latestTime || matchTime > latestTime) {
              latestTime = matchTime;
            }
          }
        }
      }

      setDependentStageLastMatchTime(latestTime);
    } catch (error) {
      console.error("Error fetching dependent stage matches:", error);
    }
  };

  const previewMatchesFn = async () => {
    setPreviewLoading(true);
    try {
      let teamIds = selectedTeams;
      const teamGroups: { [teamId: string]: string } = {};

      if (createWithEmptyTeams && stageDependent) {
        teamIds = [];
      } else if (stageDependent) {
        const topTeamIds: string[] = [];
        for (const stg of selectedStages) {
          const res = await getStandings(stg.stageName, stg.id);
          if (stg.stageName === "League") {
            const leagueTeamIds = (res as ILeagueStanding[]).filter((_, id) => id < stg.value).map((x) => x.teamId);
            topTeamIds.push(...leagueTeamIds);
          }
          if (stg.stageName === "Group") {
            if (rankedGroupTeams.length === 0) await fetchAndRankGroupTeams(stg.id);
            const fromPosition = stg.fromPosition || 1;
            const toPosition = stg.toPosition || 1;
            const qualifiedTeams = rankedGroupTeams
              .filter((team, index) => {
                const overallPosition = index + 1;
                return overallPosition >= fromPosition && overallPosition <= toPosition;
              })
              .map((team) => {
                teamGroups[team.teamId] = team.groupName;
                return team.teamId;
              });
            topTeamIds.push(...qualifiedTeams);
          }
        }
        teamIds = topTeamIds;
        setSelectedTeams(teamIds);
      }

      if (stage?.formatName === "Knockout") {
        const payload: any = {
          teamIds: createWithEmptyTeams ? [] : teamIds,
          bestOf: 1,
          useSeeding: selectedType === "seeded",
          isRandom: selectedType === "seeded" || selectedType === "random" ? true : isRandomMatches,
        };
        if (stageDependent && Object.keys(teamGroups).length > 0 && !createWithEmptyTeams) {
          payload.teamGroups = teamGroups;
        }
        if (createWithEmptyTeams && stageDependent && selectedStages.length > 0) {
          payload.qualificationRules = selectedStages.map((stg) => ({
            precedingStageId: stg.id,
            fromPosition: stg.fromPosition || 1,
            toPosition: stg.toPosition || 1,
          }));
          payload.teamIds = selectedStages.map((stg) => Array(stg.value || 1).fill("")).flat();
        }

        const res = await previewKnockoutMatches(stage.id, payload);
        setMatches(res.data);
        setBracketMatches(
          res.data.map((match: IMatchDetailDTO) => {
            let updatedMatch = { ...match };
            if (updatedMatch.parentMatch1Id !== null && updatedMatch.parentMatch2Id !== null) {
              if (updatedMatch.round === "Quarterfinals") {
                const roundOf16Matches = res.data.filter((x: any) => x.round === "Round of 16");
                updatedMatch.homeTeamName = `Winner of R16-${roundOf16Matches.findIndex((x: IMatchDetailDTO) => x.id === updatedMatch.parentMatch1Id) + 1}`;
                updatedMatch.awayTeamName = `Winner of R16-${roundOf16Matches.findIndex((x: IMatchDetailDTO) => x.id === updatedMatch.parentMatch2Id) + 1}`;
              }
              if (updatedMatch.round === "Semifinals") {
                const quarterFinals = res.data.filter((x: any) => x.round == "Quarterfinals");
                updatedMatch.homeTeamName = `Winner of QF${quarterFinals.findIndex((x: IMatchDetailDTO) => x.id === updatedMatch.parentMatch1Id) + 1}`;
                updatedMatch.awayTeamName = `Winner of QF${quarterFinals.findIndex((x: IMatchDetailDTO) => x.id === updatedMatch.parentMatch2Id) + 1}`;
              }
              if (updatedMatch.round === "Final") {
                updatedMatch.homeTeamName = "Winner of SF1";
                updatedMatch.awayTeamName = "Winner of SF2";
              }
              if (updatedMatch.round === "Third Place") {
                updatedMatch.homeTeamName = "Loser of SF1";
                updatedMatch.awayTeamName = "Loser of SF2";
              }
            }
            return updatedMatch;
          }),
        );
        const uniqueRounds = Array.from(new Set<string>(res.data.map((x: IMatchDetailDTO) => x.round)));
        if (uniqueRounds.length > 0) {
          setActiveRound({ name: uniqueRounds[0], order: 0 });
          setActiveSchedulingRound({ name: uniqueRounds[0], order: 0 });
        }
      }

      if (stage?.formatName === "League") {
        const res = await previewLeagueMatches(stage.id, { teamIds, bestOf: 1 });
        setMatches(res.data);
        const uniqueRounds = Array.from(new Set<string>(res.data.map((x: IMatchDetailDTO) => x.round)));
        if (uniqueRounds.length > 0) setActiveSchedulingRound({ name: uniqueRounds[0], order: 0 });
      }

      if (stage?.formatName === "Group") {
        const res = await previewGroupMatches(stage.id, {
          teamIds,
          isRandom: selectedType === "random" && groups.length === 0,
          groups,
          numOfGroups,
          bestOf: 1,
        });
        setMatches(res.data);
        const uniqueRounds = Array.from(new Set<string>(res.data.map((x: IMatchDetailDTO) => x.round)));
        if (uniqueRounds.length > 0) setActiveSchedulingRound({ name: uniqueRounds[0], order: 0 });
      }

      // Mexicano never previews: it is set up by MexicanoSetupStep and run from the referee portal.
      if (stage?.formatName === "Americano") {
        // Units are backend-derived (teamIds: null); the response is every round. Collect the
        // unit ids from all four team slots so the create call can enroll them (AmericanoTeams).
        const previewFn = previewAmericanoMatches;
        const courts = americanoCourts ?? null;
        let res = await previewFn(stage!.id, { teamIds: null, courts, rounds: americanoRounds ?? null });
        const collectUnits = (data: IMatchDetailDTO[]) => {
          const ids = new Set<string>();
          data.forEach((m) => {
            [m.homeTeamId, m.homeTeam2Id, m.awayTeamId, m.awayTeam2Id].forEach((tid) => {
              if (tid) ids.add(tid);
            });
          });
          return ids;
        };
        const unitIds = collectUnits(res.data);
        setAmericanoUnitCount(unitIds.size);
        // On first load default rounds to a full cycle for this player count + courts, and
        // regenerate so the admin sees that default already applied.
        if (americanoRounds == null && !americanoRoundsManual) {
          const fullCycle = americanoFullCycleRounds(unitIds.size, americanoCourts);
          setAmericanoRounds(fullCycle);
          const generatedRounds = new Set(res.data.map((x) => x.round)).size;
          if (stage!.formatName === "Americano" && generatedRounds !== fullCycle) {
            res = await previewFn(stage!.id, { teamIds: null, courts, rounds: fullCycle });
          }
        }
        setMatches(withAutoCourts(res.data));
        setSelectedTeams(Array.from(unitIds));
        const uniqueRounds = Array.from(new Set<string>(res.data.map((x: IMatchDetailDTO) => x.round)));
        if (uniqueRounds.length > 0) setActiveSchedulingRound({ name: uniqueRounds[0], order: 0 });
      }
      setFrequencyDirty(true);
    } catch (error: any) {
      console.error("Error previewing matches");
      if (stage?.formatName === "Americano") {
        // Backend returns 400 with a message for: registration not closed, < 4 units,
        // mixed-pairing gender imbalance / missing gender, wrong unit member counts.
        // The api interceptor rejects with the response body, so the message is top-level.
        await alert(error?.message || "Error previewing matches");
      }
    } finally {
      setPreviewLoading(false);
    }
  };

  // Courts drives both the per-round match cap and the full-cycle rounds default; when the
  // admin hasn't overridden rounds, re-default them from the player count + new courts.
  const updateAmericanoCourts = (courts: number | undefined) => {
    setAmericanoCourts(courts);
    if (!americanoRoundsManual) setAmericanoRounds(americanoFullCycleRounds(americanoUnitCount, courts));
  };
  const updateAmericanoRounds = (rounds: number | undefined) => {
    setAmericanoRounds(rounds);
    setAmericanoRoundsManual(true);
  };

  // Re-run the Americano/Mexicano preview with the current courts + rounds. Replaces the
  // generated matches; the admin then (re)assigns times in the scheduling step.
  const regenerateAmericano = async () => {
    const courts = americanoCourts;
    const rounds = americanoRounds;
    setPreviewLoading(true);
    try {
      const res = await previewAmericanoMatches(stage!.id, { teamIds: null, courts: courts ?? null, rounds: rounds ?? null });
      const unitIds = new Set<string>();
      res.data.forEach((m) => {
        [m.homeTeamId, m.homeTeam2Id, m.awayTeamId, m.awayTeam2Id].forEach((tid) => {
          if (tid) unitIds.add(tid);
        });
      });
      setAmericanoUnitCount(unitIds.size);
      setMatches(withAutoCourts(res.data));
      setSelectedTeams(Array.from(unitIds));
      const uniqueRounds = Array.from(new Set<string>(res.data.map((x) => x.round)));
      if (uniqueRounds.length > 0) setActiveSchedulingRound({ name: uniqueRounds[0], order: 0 });
      setFrequencyDirty(true);
    } catch (error: any) {
      await alert(error?.message || "Error regenerating schedule");
    } finally {
      setPreviewLoading(false);
    }
  };

  // --- Match management ---

  const handleMatchChange = (matchIdParam: string, field: string, value: string) => {
    setBracketMatches((prev) => {
      let updatedMatches = [...prev];
      updatedMatches = updatedMatches.map((match) => {
        if (match.id === matchIdParam) return match;
        const updatedMatch = { ...match };
        if (updatedMatch.homeTeamId === value) {
          updatedMatch.homeTeamId = "";
          updatedMatch.homeTeamName = "";
        }
        if (updatedMatch.awayTeamId === value) {
          updatedMatch.awayTeamId = "";
          updatedMatch.awayTeamName = "";
        }
        if (updatedMatch.homeTeamName === value) updatedMatch.homeTeamName = "";
        if (updatedMatch.awayTeamName === value) updatedMatch.awayTeamName = "";
        return updatedMatch;
      });
      const idx = updatedMatches.findIndex((x) => x.id === matchIdParam);
      if (idx !== -1) {
        const match = { ...updatedMatches[idx], [field]: value };
        if (field === "homeTeamId") {
          match.homeTeamName = getTeamNameById(value);
          if (match.awayTeamId === value) {
            match.awayTeamId = "";
            match.awayTeamName = "";
          }
        }
        if (field === "awayTeamId") {
          match.awayTeamName = getTeamNameById(value);
          if (match.homeTeamId === value) {
            match.homeTeamId = "";
            match.homeTeamName = "";
          }
        }
        if (field === "homeTeamName" && match.awayTeamName === value) match.awayTeamName = "";
        if (field === "awayTeamName" && match.homeTeamName === value) match.homeTeamName = "";
        updatedMatches[idx] = match;
      }
      return updatedMatches;
    });
  };

  const getTeamNameById = (teamId: string): string => {
    const team = stage?.teams.find((t) => t.id === teamId);
    return team ? team.teamName : "";
  };

  const getSelectableTeams = (roundName: string) => {
    if (activeRound?.order === 0) {
      return selectedTeams.map((item) => ({
        key: `id-${item}`,
        label: stage?.teams.find((x) => x.id === item)?.teamName || "Unknown",
      }));
    }
    if (roundName === "Quarterfinals") {
      return Array.from({ length: 8 }, (_, i) => ({
        key: `name-Winner of R16-${i + 1}`,
        label: `Winner of R16-${i + 1}`,
      }));
    }
    if (roundName === "Semifinals") {
      return Array.from({ length: 4 }, (_, i) => ({ key: `name-Winner of QF${i + 1}`, label: `Winner of QF${i + 1}` }));
    }
    if (roundName === "Final") {
      return [
        { key: `name-Winner of SF1`, label: `Winner of SF1` },
        { key: `name-Winner of SF2`, label: `Winner of SF2` },
      ];
    }
    if (roundName === "Third Place") {
      return [
        { key: `name-Loser of SF1`, label: `Loser of SF1` },
        { key: `name-Loser of SF2`, label: `Loser of SF2` },
      ];
    }
    return [];
  };

  const getTeamGroup = (teamId: string): number => {
    const groupIndex = groups.findIndex((group) => group.teamIds.includes(teamId));
    return groupIndex !== -1 ? groupIndex : -1;
  };

  const handleGroupChange = (teamId: string, groupIndex: number) => {
    setGroups((prevGroups) => {
      const cleanedGroups = prevGroups.map((group) => ({
        ...group,
        teamIds: group.teamIds.filter((tid) => tid !== teamId),
      }));
      const updatedGroups = [];
      for (let i = 0; i < numOfGroups; i++) {
        const existingGroup = cleanedGroups[i];
        if (existingGroup) updatedGroups.push(existingGroup);
        else updatedGroups.push({ groupName: getLetterByIndex(i), teamIds: [] });
      }
      if (groupIndex >= 0 && groupIndex < numOfGroups) {
        const targetGroup = updatedGroups[groupIndex];
        if (!targetGroup.teamIds.includes(teamId)) targetGroup.teamIds.push(teamId);
      }
      return updatedGroups;
    });
  };

  const matchChangeHandler = (matchId: string, field: string, value: string | Date | null) => {
    setMatches((prev) => {
      const updatedMatches = [...prev];
      const idx = updatedMatches.findIndex((x) => x.id === matchId);
      if (idx !== -1) {
        updatedMatches[idx] = { ...updatedMatches[idx], [field]: value };
      }
      return updatedMatches;
    });
  };

  // --- Create handlers ---

  const createKnockoutMatchesHandler = async () => {
    setCreateLoading(true);
    try {
      const res = await createKnockoutMatches(stage?.id!, matches);
      if (res.data)
        navigate(`/dashboard/tournaments/${id}/${tournamentCategoryId}/stages/${stageId}`, { replace: true });
    } catch (error: any) {
      console.error("Error creating knockout matches:", error.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const createGroupMatchesHandler = async () => {
    setCreateLoading(true);
    try {
      const teamGroupMapping = selectedTeams.map((team) => {
        const teamGroup = groups.find((group) => group.teamIds.includes(team));
        return { teamId: team, groupName: teamGroup?.groupName || "A" };
      });
      const res = await createGroupMatches(stage?.id!, matches, teamGroupMapping);
      if (res.data)
        navigate(`/dashboard/tournaments/${id}/${tournamentCategoryId}/stages/${stageId}`, { replace: true });
    } catch (error: any) {
      console.error("Error creating group matches:", error.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const createLeagueMatchesHandler = async () => {
    setCreateLoading(true);
    try {
      const res = await createLeagueMatches(stage?.id!, matches, selectedTeams);
      if (res.data)
        navigate(`/dashboard/tournaments/${id}/${tournamentCategoryId}/stages/${stageId}`, { replace: true });
    } catch (error: any) {
      console.error("Error creating league matches:", error.message);
    } finally {
      setCreateLoading(false);
    }
  };

  const createAmericanoMatchesHandler = async () => {
    setCreateLoading(true);
    try {
      const res = await createAmericanoMatches(stage?.id!, matches, selectedTeams, {
        courts: americanoCourts ?? null,
        rounds: americanoRounds ?? null,
      });
      if (res.data)
        navigate(`/dashboard/tournaments/${id}/${tournamentCategoryId}/stages/${stageId}`, { replace: true });
    } catch (error: any) {
      console.error("Error creating americano/mexicano matches:", error.message);
      await alert(error?.message || "Error creating schedule");
    } finally {
      setCreateLoading(false);
    }
  };

  // --- Scheduling dispatcher ---

  const updateMatchesFrequency = (): boolean => {
    const result: IMatchDetailDTO[] = matches.map((m) => ({ ...m }));
    const configDates = Object.keys(matchConfigs).sort();

    const validationError = validateMatchConfigurations({
      matches: result,
      configDates,
      formatName: stage?.formatName || "",
      groupDateAssignments,
      matchConfigs,
      selectedType,
    });
    if (validationError) {
      void alert(validationError);
      return false;
    }

    const standardScheduler = (m: IMatchDetailDTO[], d: string[]) =>
      scheduleMatchesStandard({ result: m, configDates: d, matchConfigs });

    if (stage?.formatName === "Group") {
      const hasGroupSpecificDates = Object.keys(groupDateAssignments).some(
        (groupId) => groupDateAssignments[groupId].dates.length > 0,
      );
      const hasVenueAssignments = Object.keys(groupVenueAssignments).length > 0;

      if (hasGroupSpecificDates) {
        scheduleGroupMatchesWithSpecificDates({
          result,
          groupDateAssignments,
          matchConfigs,
          groupVenueAssignments,
          scheduleStandard: standardScheduler,
        });
      } else if (hasVenueAssignments) {
        scheduleGroupMatchesWithVenues({ result, configDates, matchConfigs, groupVenueAssignments, venueRange });
      } else {
        scheduleGroupMatchesStandard({ result, configDates, matchConfigs });
      }
    } else if (stage?.formatName === "Knockout") {
      if (Object.keys(knockoutVenueAssignments).length > 0) {
        scheduleKnockoutMatchesWithVenues({ result, configDates, matchConfigs, knockoutVenueAssignments });
      } else {
        scheduleMatchesStandard({ result, configDates, matchConfigs });
      }
    } else if (stage?.formatName === "League") {
      if (leagueVenueAssignments.venueIds.length > 0) {
        scheduleLeagueMatchesWithVenues({
          result,
          configDates,
          matchConfigs,
          leagueVenueAssignments,
          scheduleStandard: standardScheduler,
        });
      } else {
        scheduleMatchesStandard({ result, configDates, matchConfigs });
      }
    } else if (isAmericanoFormat(stage?.formatName)) {
      // A round is played simultaneously across courts: one time slot per round.
      scheduleAmericanoRounds({ result, configDates, matchConfigs });
    } else {
      scheduleMatchesStandard({ result, configDates, matchConfigs });
    }

    setMatches(result);
    setFrequencyDirty(false);
    return true;
  };

  // --- Display helpers ---

  const getRoundAbbr = (round: string) => {
    switch (round) {
      case "Round of 16":
        return "R16-";
      case "Quarterfinals":
        return "QF";
      case "Semifinals":
        return "SF";
      default:
        return "Match ";
    }
  };

  const getDependentMatch = (matchId: string, isThirdPlace: boolean) =>
    getDependentMatchLabel(matches, matchId, isThirdPlace);

  // --- Navigation ---

  const nextHandler = async (manualConfig: boolean = false) => {
    if (stage?.formatName === "League") {
      if (currentStep === "dependent-stages") {
        if (stageDependent && selectedStages.length > 0) {
          setActiveStageTab(stage?.precedingStages.find((x) => x.id === selectedStages[0].id));
          setCurrentStep("qualification-rules");
        } else if (!stageDependent && selectedTeams.length > 0) {
          setMatches([]);
          previewMatchesFn();
          setCurrentStep("match-frequency");
        }
      } else if (currentStep === "qualification-rules") {
        setMatches([]);
        previewMatchesFn();
        if (stageDependent) fetchDependentStageLastMatchTime();
        setCurrentStep("match-frequency");
      } else if (currentStep === "match-frequency") {
        if (frequencyDirty) {
          if (!updateMatchesFrequency()) return;
        }
        setCurrentStep("match-scheduling");
      } else if (currentStep === "match-scheduling") {
        await createLeagueMatchesHandler();
      }
    }

    if (stage?.formatName === "Group") {
      if (currentStep === "dependent-stages") {
        if (stageDependent && selectedStages.length > 0) {
          setActiveStageTab(stage?.precedingStages.find((x) => x.id === selectedStages[0].id));
          setCurrentStep("qualification-rules");
        } else if (!stageDependent && selectedTeams.length > 0) {
          setCurrentStep("scheduling-type");
        }
      } else if (currentStep === "scheduling-type") {
        if (selectedType === "manual") {
          setNumOfGroups(1);
          setGroups([{ groupName: "A", teamIds: [] }]);
          setCurrentStep("manage-groups");
        } else {
          setMatches([]);
          previewMatchesFn();
          if (stageDependent) fetchDependentStageLastMatchTime();
          setCurrentStep("match-frequency");
        }
      } else if (currentStep === "qualification-rules") {
        setCurrentStep("scheduling-type");
      } else if (currentStep === "manage-groups") {
        setMatches([]);
        previewMatchesFn();
        if (stageDependent) fetchDependentStageLastMatchTime();
        setCurrentStep("match-frequency");
      } else if (currentStep === "match-frequency") {
        if (!manualConfig && frequencyDirty) {
          if (!updateMatchesFrequency()) return;
        }
        setCurrentStep("match-scheduling");
      } else if (currentStep === "match-scheduling") {
        await createGroupMatchesHandler();
      }
    }

    if (stage?.formatName === "Knockout") {
      if (currentStep === "dependent-stages") {
        if (stageDependent && selectedStages.length > 0) {
          setActiveStageTab(stage?.precedingStages.find((x) => x.id === selectedStages[0].id));
          setCurrentStep("qualification-rules");
        } else if (!stageDependent && selectedTeams.length > 0) {
          setSelectedType("random");
          setCurrentStep("scheduling-type");
        }
      } else if (currentStep === "qualification-rules") {
        setSelectedType("random");
        setCurrentStep("scheduling-type");
      } else if (currentStep === "scheduling-type") {
        setMatches([]);
        await previewMatchesFn();
        if (selectedType === "manual") {
          setIsRandomMatches(false);
          setCurrentStep("manage-bracket");
        } else {
          if (stageDependent) fetchDependentStageLastMatchTime();
          setCurrentStep("match-frequency");
        }
      } else if (currentStep === "manage-bracket") {
        setMatches(bracketMatches);
        if (stageDependent) fetchDependentStageLastMatchTime();
        setCurrentStep("match-frequency");
      } else if (currentStep === "match-frequency") {
        if (frequencyDirty) {
          if (!updateMatchesFrequency()) return;
        }
        setCurrentStep("match-scheduling");
      } else if (currentStep === "match-scheduling") {
        await createKnockoutMatchesHandler();
      }
    }

    if (stage?.formatName === "Americano") {
      if (currentStep === "match-frequency") {
        // Generate the schedule with the chosen courts + rounds, then let the admin set
        // round times in the scheduling step. Times are NOT set here (no date grid).
        await regenerateAmericano();
        setCurrentStep("match-scheduling");
      } else if (currentStep === "match-scheduling") {
        await createAmericanoMatchesHandler();
      }
    }
  };

  const backHandler = () => {
    if (currentStep === "dependent-stages") return;

    if (stage?.formatName === "League") {
      if (currentStep === "qualification-rules") setCurrentStep("dependent-stages");
      if (currentStep === "match-scheduling") setCurrentStep("match-frequency");
      if (currentStep === "match-frequency")
        setCurrentStep(stageDependent ? "qualification-rules" : "dependent-stages");
    }

    if (stage?.formatName === "Group") {
      if (currentStep === "qualification-rules") setCurrentStep("dependent-stages");
      if (currentStep === "scheduling-type")
        setCurrentStep(stageDependent ? "qualification-rules" : "dependent-stages");
      if (currentStep === "manage-groups") setCurrentStep("scheduling-type");
      if (currentStep === "match-frequency")
        setCurrentStep(selectedType === "manual" ? "manage-groups" : "scheduling-type");
      if (currentStep === "match-scheduling") setCurrentStep("match-frequency");
    }

    if (stage?.formatName === "Knockout") {
      if (currentStep === "qualification-rules") setCurrentStep("dependent-stages");
      if (currentStep === "scheduling-type")
        setCurrentStep(stageDependent ? "qualification-rules" : "dependent-stages");
      if (currentStep === "manage-bracket") setCurrentStep("scheduling-type");
      if (currentStep === "match-frequency")
        setCurrentStep(selectedType === "manual" ? "manage-bracket" : "scheduling-type");
      if (currentStep === "match-scheduling") setCurrentStep("match-frequency");
    }

    if (stage?.formatName === "Americano") {
      // match-frequency is the initial step for Americano; only one Back hop exists.
      if (currentStep === "match-scheduling") setCurrentStep("match-frequency");
    }
  };

  // --- Effects ---

  // Redistribute matchesPerDay when matches load (handles dates added before preview completes)
  useEffect(() => {
    if (matches.length > 0 && selectedDates.length > 0) {
      setMatchConfigs((prev) => {
        const dateStrs = Object.keys(prev);
        if (dateStrs.length === 0) return prev;

        const currentTotal = dateStrs.reduce((sum, ds) => sum + (prev[ds]?.matchesPerDay || 0), 0);
        if (currentTotal === matches.length) return prev; // Already correct, preserve manual edits

        const basePerDay = Math.floor(matches.length / dateStrs.length);
        let remainder = matches.length % dateStrs.length;
        const updated = { ...prev };
        for (const ds of dateStrs) {
          updated[ds] = { ...updated[ds], matchesPerDay: basePerDay + (remainder > 0 ? 1 : 0) };
          if (remainder > 0) remainder--;
        }
        return updated;
      });
    }
  }, [matches.length]);

  useEffect(() => {
    if (numOfGroups > 0 && selectedTeams.length > 0 && selectedType !== "manual") {
      const newGroups: GroupDefinition[] = [];
      for (let i = 0; i < numOfGroups; i++) {
        newGroups.push({ groupName: getLetterByIndex(i), teamIds: [] });
      }
      selectedTeams.forEach((teamId, index) => {
        const groupIndex = index % numOfGroups;
        newGroups[groupIndex].teamIds.push(teamId);
      });
      setGroups(newGroups);
    }
  }, [numOfGroups, selectedTeams, selectedType]);

  useEffect(() => {
    if (selectedType !== "manual") {
      setGroups((prevGroups) => {
        if (selectedTeams.length > 0 && (prevGroups.length === 0 || prevGroups.every((g) => g.teamIds.length === 0))) {
          return [{ groupName: "A", teamIds: selectedTeams }];
        }
        return prevGroups;
      });
    } else {
      setGroups((prevGroups) => {
        if (prevGroups.length > 0) return prevGroups.map((group) => ({ ...group, teamIds: [] }));
        return prevGroups;
      });
    }
  }, [selectedTeams, selectedType]);

  useEffect(() => {
    if (activeStageTab?.stageName === "Group" && currentStep === "qualification-rules" && !createWithEmptyTeams) {
      fetchAndRankGroupTeams(activeStageTab.id);
    }
  }, [activeStageTab, currentStep, createWithEmptyTeams]);

  useEffect(() => {
    setGroups((prevGroups) => {
      const trimmedGroups = prevGroups.slice(0, numOfGroups);
      const removedGroups = prevGroups.slice(numOfGroups);
      const removedTeamIds = removedGroups.flatMap((group) => group.teamIds);
      const filledGroups = [...trimmedGroups];
      while (filledGroups.length < numOfGroups) {
        filledGroups.push({ groupName: getLetterByIndex(filledGroups.length), teamIds: [] });
      }
      if (selectedType !== "manual" && removedTeamIds.length > 0 && filledGroups.length > 0) {
        removedTeamIds.forEach((teamId) => {
          if (!filledGroups[0].teamIds.includes(teamId)) filledGroups[0].teamIds.push(teamId);
        });
      }
      return filledGroups;
    });
  }, [numOfGroups, selectedType]);

  useEffect(() => {
    previewMatchesFn();
  }, [isRandomMatches]);
  useEffect(() => {
    getStageOverview();
  }, []);
  useEffect(() => {
    // Americano stages have no team-selection steps: the backend derives units itself, so
    // the wizard opens directly on match-frequency with the preview already loading.
    // (Mexicano skips the wizard entirely: see MexicanoSetupStep.)
    if (stage?.formatName === "Americano") {
      setStageDependent(false);
      setMatches([]);
      previewMatchesFn();
      setCurrentStep("match-frequency");
    }
  }, [stage?.id]);
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [currentStep]);

  return {
    // Navigation & loading
    loading,
    createLoading,
    previewLoading,
    rankedTeamsLoading,
    currentStep,
    stage,
    navigate,
    searchParams,
    id,
    stageId,
    tournamentCategoryId,

    // Step state
    stageDependent,
    setStageDependent,
    activeStageTab,
    setActiveStageTab,
    selectedStages,
    setSelectedStages,
    selectedTeams,
    setSelectedTeams,
    numOfGroups,
    setNumOfGroups,
    createWithEmptyTeams,
    setCreateWithEmptyTeams,
    rankedGroupTeams,
    selectedType,
    setSelectedType,
    groups,
    setGroups,
    bracketMatches,
    isRandomMatches,
    setIsRandomMatches,
    matches,
    activeRound,
    setActiveRound,
    activeSchedulingRound,
    setActiveSchedulingRound,

    // Americano/Mexicano config
    americanoCourts,
    americanoRounds,
    americanoUnitCount,
    updateAmericanoCourts,
    updateAmericanoRounds,
    regenerateAmericano,

    // Date & config
    selectedDates,
    matchConfigs,
    groupDateAssignments,
    venueRange,
    setVenueRange,
    groupVenueAssignments,
    knockoutVenueAssignments,
    leagueVenueAssignments,

    // Handlers
    addDate,
    removeDate,
    updateConfig,
    addGroupDate,
    removeGroupDate,
    updateGroupVenueAssignment,
    autoAssignVenuesToGroups,
    updateKnockoutVenueAssignment,
    autoAssignVenuesToKnockoutRounds,
    updateLeagueVenueAssignment,
    autoAssignVenuesToLeague,
    handleMatchChange,
    handleGroupChange,
    matchChangeHandler,
    getTeamNameById,
    getSelectableTeams,
    getTeamGroup,
    calculateTotalVenuesNeeded,
    fetchAndRankGroupTeams,

    // Navigation
    nextHandler,
    backHandler,

    // Display helpers
    getRoundAbbr,
    getDependentMatch,
  };
};
